import { ShareApi } from '@repo/api'
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
  Progress,
  Skeleton,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
  useIsMobile,
} from '@repo/ui'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { format, parseISO } from 'date-fns'
import { AnimatePresence, motion } from 'framer-motion'
import {
  Box,
  Clock,
  Flag,
  Fuel,
  Gauge,
  Layers,
  MapPin,
  Maximize2,
  Milestone,
  Minimize2,
  Minus,
  Moon,
  Mountain,
  Plus,
  Satellite,
  Settings2,
  Sun,
  Truck,
  X,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { LANGUAGE_KEY, changeLanguage } from '../../i18n'

import type { HereMapHandle, MapVehicle } from '@/components/here-map/types'
import type { RouteShareData, VehicleShareData } from '@repo/api'

import BosniaFlag from '@/assets/flags/bih.svg?react'
import GermanFlag from '@/assets/flags/deutch.svg?react'
import EnglishFlag from '@/assets/flags/english.svg?react'
import SerbiaFlag from '@/assets/flags/serbia.svg?react'
import { isRasterStyle } from '@/components/here-map/constants'
import { HereMap } from '@/components/here-map/here-map'
import { MapStyle } from '@/components/here-map/types'
import { useDocumentTitle } from '@/hooks/use-document-title'
import { localizedPageTitle } from '@/lib/page-title'
import { STATUS_COLORS } from '@/lib/status-colors'

export const Route = createFileRoute('/share/$token')({
  head: () => ({ meta: [{ title: localizedPageTitle('vehicleLocation') }] }),
  component: SharePage,
})

function mapShareDataToVehicle(data: VehicleShareData): MapVehicle | null {
  const { telemetry } = data
  if (!telemetry || !telemetry.latitude || !telemetry.longitude) return null

  const isMoving = telemetry.ignitionStatus && (telemetry.speed ?? 0) > 0
  const markerColor = isMoving
    ? STATUS_COLORS.active
    : telemetry.ignitionStatus
      ? STATUS_COLORS.idle
      : STATUS_COLORS.offline

  return {
    id: String(data.vehicleId),
    plateNumber: data.licensePlate,
    model: [data.manufacturer, data.vehicleModel].filter(Boolean).join(' '),
    type: 'truck',
    status: 'active',
    location: {
      lat: telemetry.latitude,
      lng: telemetry.longitude,
      address: '',
    },
    fuel: telemetry.fuelLevel ?? 0,
    mileage: 0,
    lastUpdate: telemetry.timestamp,
    markerColor,
    speed: telemetry.speed,
    direction: telemetry.direction,
    workState: null,
  }
}

function routeTruckToVehicle(data: RouteShareData): MapVehicle | null {
  const tk = data.truck
  if (!tk) return null

  const isMoving = tk.ignitionStatus && (tk.speed ?? 0) > 0
  const markerColor = data.isStale
    ? STATUS_COLORS.offline
    : isMoving
      ? STATUS_COLORS.active
      : tk.ignitionStatus
        ? STATUS_COLORS.idle
        : STATUS_COLORS.offline

  return {
    id: 'route-truck',
    plateNumber: data.destinationLabel ?? '',
    model: '',
    type: 'truck',
    status: 'active',
    location: { lat: tk.latitude, lng: tk.longitude, address: '' },
    fuel: 0,
    mileage: 0,
    lastUpdate: tk.timestamp,
    markerColor,
    speed: tk.speed,
    direction: tk.direction,
    workState: null,
  }
}

const MAP_STYLES: Array<{
  value: MapStyle
  labelKey: 'styleExplore' | 'styleNight' | 'styleSatellite' | 'styleTerrain'
  icon: React.ReactNode
}> = [
  {
    value: MapStyle.ExploreDay,
    labelKey: 'styleExplore',
    icon: <Sun className="h-4 w-4" />,
  },
  {
    value: MapStyle.ExploreNight,
    labelKey: 'styleNight',
    icon: <Moon className="h-4 w-4" />,
  },
  {
    value: MapStyle.Satellite,
    labelKey: 'styleSatellite',
    icon: <Satellite className="h-4 w-4" />,
  },
  {
    value: MapStyle.Terrain,
    labelKey: 'styleTerrain',
    icon: <Mountain className="h-4 w-4" />,
  },
]

/** Languages offered on the public share page (mirrors the in-app switcher). */
const SHARE_LANGUAGES: Array<{
  code: 'ba' | 'rs' | 'en' | 'de'
  nameKey: 'langBosnian' | 'langSerbian' | 'langEnglish' | 'langGerman'
  Flag: React.FC<React.SVGProps<SVGSVGElement>>
}> = [
  { code: 'ba', nameKey: 'langBosnian', Flag: BosniaFlag },
  { code: 'rs', nameKey: 'langSerbian', Flag: SerbiaFlag },
  { code: 'en', nameKey: 'langEnglish', Flag: EnglishFlag },
  { code: 'de', nameKey: 'langGerman', Flag: GermanFlag },
]

/** Best-effort map of the recipient's browser locale to one of our 4 languages.
 *  de → de, sr → rs, bs/hr → ba, everything else → en. */
function detectBrowserLanguage(): 'ba' | 'rs' | 'en' | 'de' {
  const nav =
    typeof navigator !== 'undefined' ? navigator.language.toLowerCase() : ''
  if (nav.startsWith('de')) return 'de'
  if (nav.startsWith('sr')) return 'rs'
  if (nav.startsWith('bs') || nav.startsWith('hr')) return 'ba'
  return 'en'
}

/** Compact language picker for the public page. Anchored top-left, clear of the
 *  map controls (top-right / bottom-right) and the info card (bottom). */
function ShareLanguageSwitcher() {
  const { t, i18n } = useTranslation('share')
  const current = i18n.language.slice(0, 2)
  const active =
    SHARE_LANGUAGES.find((l) => l.code === current) ?? SHARE_LANGUAGES[2]

  return (
    <div className="absolute top-3 left-3 z-20">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            aria-label={t('language')}
            className="bg-background/90 border-border gap-2 px-3 shadow-lg backdrop-blur-sm has-[>svg]:px-3"
          >
            <active.Flag className="size-4 shrink-0" />
            <span className="text-xs font-semibold uppercase">
              {active.code}
            </span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" sideOffset={6} className="w-44">
          {SHARE_LANGUAGES.map((lang) => (
            <DropdownMenuItem
              key={lang.code}
              onClick={() => changeLanguage(lang.code)}
              className={cn('gap-2.5', lang.code === current && 'bg-muted')}
            >
              <lang.Flag className="size-4 shrink-0" />
              <span className="text-sm font-medium">{t(lang.nameKey)}</span>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}

function ShareMapControls({
  mapRef,
}: {
  mapRef: React.RefObject<HereMapHandle | null>
}) {
  const { t } = useTranslation('share')
  const isMobile = useIsMobile()
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [is3D, setIs3D] = useState(false)
  const [activeStyle, setActiveStyle] = useState<MapStyle>(MapStyle.ExploreDay)
  const [expanded, setExpanded] = useState(true)
  const [zoom, setZoom] = useState<number>(() => mapRef.current?.getZoom() ?? 0)
  const can3D = zoom > 15

  useEffect(() => {
    setExpanded(!isMobile)
  }, [isMobile])

  const isRaster = isRasterStyle(activeStyle)

  useEffect(() => {
    const onChange = () => setIsFullscreen(!!document.fullscreenElement)
    document.addEventListener('fullscreenchange', onChange)
    return () => document.removeEventListener('fullscreenchange', onChange)
  }, [])

  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    const initial = map.getZoom()
    if (typeof initial === 'number') setZoom(initial)
    return map.addZoomListener((z) => setZoom(z))
  }, [mapRef])

  useEffect(() => {
    if (!can3D && is3D) {
      setIs3D(false)
      mapRef.current?.toggle3D(false)
    }
  }, [can3D, is3D, mapRef])

  const handleFullscreen = useCallback(() => {
    const container = mapRef.current
      ?.getMapContainer()
      ?.closest('[data-share-map]') as HTMLElement | null
    if (!container) return

    if (document.fullscreenElement) {
      document.exitFullscreen()
    } else {
      container.requestFullscreen()
    }
  }, [mapRef])

  const handleSetStyle = useCallback(
    (newStyle: MapStyle) => {
      mapRef.current?.setMapStyle(newStyle)
      setActiveStyle(newStyle)
      const switchingToRaster = isRasterStyle(newStyle)
      if (switchingToRaster && is3D) {
        setIs3D(false)
        mapRef.current?.toggle3D(false)
      }
    },
    [mapRef, is3D],
  )

  return (
    <AnimatePresence mode="wait" initial={false}>
      {isMobile && !expanded ? (
        <motion.button
          key="collapsed"
          type="button"
          onClick={() => setExpanded(true)}
          aria-label={t('mapControls')}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          transition={{ duration: 0.15, ease: 'easeOut' }}
          style={{ originX: 1, originY: 0 }}
          className="bg-background/95 border-border absolute top-3 right-3 z-20 flex size-10 items-center justify-center rounded-lg border shadow-lg backdrop-blur-sm"
        >
          <Settings2 className="size-4" />
        </motion.button>
      ) : (
        <motion.div
          key="expanded"
          initial={{ opacity: 0, scale: 0.85 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.85 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          style={{ originX: 1, originY: isMobile ? 0 : 1 }}
          className="bg-background/90 border-border absolute top-3 right-3 z-20 flex flex-col gap-0.5 rounded-lg border p-1 shadow-lg backdrop-blur-sm md:top-auto md:right-4 md:bottom-4"
        >
          {isMobile && (
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => setExpanded(false)}
              aria-label={t('collapse')}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => mapRef.current?.zoomIn()}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="left">{t('zoomIn')}</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => mapRef.current?.zoomOut()}
              >
                <Minus className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="left">{t('zoomOut')}</TooltipContent>
          </Tooltip>

          <div className="bg-border mx-1 h-px" />

          <HoverCard openDelay={100} closeDelay={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <HoverCardTrigger asChild>
                  <Button variant="ghost" size="icon-sm">
                    <Layers className="h-4 w-4" />
                  </Button>
                </HoverCardTrigger>
              </TooltipTrigger>
            </Tooltip>
            <HoverCardContent
              side="left"
              align="end"
              sideOffset={12}
              className="w-40 p-1"
            >
              {MAP_STYLES.map((s) => (
                <Button
                  key={s.value}
                  variant={activeStyle === s.value ? 'default' : 'ghost'}
                  size="sm"
                  className="w-full justify-start gap-2"
                  onClick={() => handleSetStyle(s.value)}
                >
                  {s.icon}
                  {t(s.labelKey)}
                </Button>
              ))}
            </HoverCardContent>
          </HoverCard>

          <div
            className="grid transition-all duration-300 ease-in-out"
            style={{
              gridTemplateRows: !isRaster ? '1fr' : '0fr',
              opacity: !isRaster ? 1 : 0,
            }}
          >
            <div className="overflow-hidden">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant={is3D ? 'default' : 'ghost'}
                    size="icon-sm"
                    disabled={!can3D}
                    onClick={() => {
                      const next = !is3D
                      setIs3D(next)
                      mapRef.current?.toggle3D(next)
                    }}
                  >
                    <Box className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="left">
                  {!can3D
                    ? t('enable3DHint')
                    : is3D
                      ? t('exit3D')
                      : t('view3D')}
                </TooltipContent>
              </Tooltip>
            </div>
          </div>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon-sm" onClick={handleFullscreen}>
                {isFullscreen ? (
                  <Minimize2 className="h-4 w-4" />
                ) : (
                  <Maximize2 className="h-4 w-4" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="left">
              {isFullscreen ? t('exitFullscreen') : t('fullscreen')}
            </TooltipContent>
          </Tooltip>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

const SHARE_TOKEN_RE = /^[a-zA-Z0-9_-]{10,}$/

function ShareStateScreen({
  icon,
  title,
  message,
}: {
  icon: React.ReactNode
  title: string
  message: string
}) {
  return (
    <div className="bg-background flex h-screen w-screen items-center justify-center">
      <div className="max-w-sm space-y-3 text-center">
        {icon}
        <h1 className="text-lg font-semibold">{title}</h1>
        <p className="text-muted-foreground text-sm">{message}</p>
      </div>
    </div>
  )
}

function SharePage() {
  useDocumentTitle('vehicleLocation')
  const { t, i18n } = useTranslation('share')
  const { token } = Route.useParams()

  // Public recipients rarely share the operator's language. If they haven't made
  // an explicit choice yet (no persisted key), align the page with their browser
  // locale. A manual pick via the switcher persists and wins on later visits.
  useEffect(() => {
    if (typeof window === 'undefined') return
    if (localStorage.getItem(LANGUAGE_KEY)) return
    const detected = detectBrowserLanguage()
    if (i18n.language.slice(0, 2) !== detected) {
      void i18n.changeLanguage(detected)
    }
  }, [i18n])

  const isValidToken = SHARE_TOKEN_RE.test(token)

  const { data, isLoading, error } = useQuery({
    queryKey: ['shareData', token],
    queryFn: () => ShareApi.getShareData(token),
    enabled: isValidToken,
    retry: false,
    refetchInterval: 3_000,
  })

  const isExpired =
    !isValidToken || (error != null && (error as any).status === 404)
  const isError = isValidToken && error != null && !isExpired

  if (isLoading) {
    return (
      <div className="relative h-screen w-screen overflow-hidden">
        <Skeleton className="absolute inset-0" />
        <div className="bg-background/90 border-border absolute right-3 bottom-3 left-3 z-20 space-y-2.5 rounded-xl border p-3 shadow-lg backdrop-blur-md md:right-auto md:bottom-4 md:left-4 md:w-[22rem] md:p-3.5">
          <Skeleton className="h-5 w-40" />
          <Skeleton className="h-4 w-full" />
        </div>
      </div>
    )
  }

  if (isExpired) {
    return (
      <ShareStateScreen
        icon={
          <Clock className="text-muted-foreground mx-auto h-12 w-12 opacity-40" />
        }
        title={t('expiredTitle')}
        message={t('expiredMessage')}
      />
    )
  }

  if (isError || !data) {
    return (
      <ShareStateScreen
        icon={
          <MapPin className="text-muted-foreground mx-auto h-12 w-12 opacity-40" />
        }
        title={t('unavailableTitle')}
        message={t('unavailableMessage')}
      />
    )
  }

  return data.kind === 'route' ? (
    <RouteShareView data={data} />
  ) : (
    <VehicleShareView data={data} />
  )
}

function VehicleShareView({ data }: { data: VehicleShareData }) {
  const { t } = useTranslation('share')
  const mapRef = useRef<HereMapHandle>(null)
  const vehicle = useMemo(() => mapShareDataToVehicle(data), [data])
  const vehicles = useMemo(() => (vehicle ? [vehicle] : []), [vehicle])

  const { telemetry, licensePlate, manufacturer, vehicleModel, expiresAt } =
    data
  const hasPosition = telemetry?.latitude != null && telemetry.longitude != null

  return (
    <div className="relative h-screen w-screen overflow-hidden">
      <div className="absolute inset-0" data-share-map>
        {hasPosition && vehicle ? (
          <>
            <HereMap
              ref={mapRef}
              vehicles={vehicles}
              center={vehicle.location}
              zoom={14}
            />
            <ShareMapControls mapRef={mapRef} />
          </>
        ) : (
          <div className="bg-muted flex h-full items-center justify-center">
            <p className="text-muted-foreground text-sm">{t('noGps')}</p>
          </div>
        )}
      </div>

      <ShareLanguageSwitcher />

      <div className="bg-background/90 border-border absolute right-3 bottom-3 left-3 z-20 space-y-3 rounded-xl border p-3 shadow-lg backdrop-blur-md md:right-auto md:bottom-4 md:left-4 md:w-fit md:max-w-md md:space-y-4 md:p-3.5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <Truck className="text-muted-foreground h-4 w-4 shrink-0" />
            <span className="truncate font-semibold">{licensePlate}</span>
            {vehicleModel && (
              <span className="text-muted-foreground hidden truncate text-sm sm:inline">
                {manufacturer} {vehicleModel}
              </span>
            )}
          </div>
          <Badge
            variant="outline"
            className={
              telemetry?.ignitionStatus
                ? 'shrink-0 border-green-500 text-green-600'
                : 'text-muted-foreground shrink-0'
            }
          >
            {telemetry?.ignitionStatus ? t('engineOn') : t('engineOff')}
          </Badge>
          {vehicleModel && (
            <span className="text-muted-foreground w-full truncate text-xs sm:hidden">
              {manufacturer} {vehicleModel}
            </span>
          )}
        </div>

        <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm md:gap-x-8 md:gap-y-3">
          <div className="flex items-center gap-1.5">
            <Gauge className="h-3.5 w-3.5 text-blue-500" />
            <span className="text-muted-foreground">{t('speed')}</span>
            <span className="font-medium">
              {telemetry?.speed != null
                ? `${Math.round(telemetry.speed)} km/h`
                : '—'}
            </span>
          </div>

          <div className="flex items-center gap-1.5">
            <Fuel className="h-3.5 w-3.5 text-amber-500" />
            <span className="text-muted-foreground">{t('fuel')}</span>
            <span className="font-medium">
              {telemetry?.fuelLevel != null
                ? `${telemetry.fuelLevel.toFixed(0)}%`
                : '—'}
            </span>
          </div>

          {telemetry?.timestamp && (
            <div className="flex items-center gap-1.5">
              <Clock className="text-muted-foreground h-3.5 w-3.5" />
              <span className="text-muted-foreground">{t('updated')}</span>
              <span className="font-medium">
                {format(parseISO(telemetry.timestamp), 'HH:mm:ss')}
              </span>
            </div>
          )}
        </div>

        <p className="text-muted-foreground border-border/60 border-t pt-2 text-xs">
          {t('linkExpires', {
            date: format(parseISO(expiresAt), 'dd.MM.yyyy HH:mm'),
          })}{' '}
          · {t('autoRefresh', { seconds: 3 })}
        </p>
      </div>
    </div>
  )
}

function RouteShareView({ data }: { data: RouteShareData }) {
  const { t } = useTranslation('share')
  const mapRef = useRef<HereMapHandle>(null)
  const truck = useMemo(() => routeTruckToVehicle(data), [data])
  const vehicles = useMemo(() => (truck ? [truck] : []), [truck])
  const routePolylines = useMemo(
    () =>
      data.polylines.length
        ? [{ polylines: data.polylines, isAlternative: false }]
        : [],
    [data.polylines],
  )
  // Endpoint pins only — intermediate stops stay private (see share.service.ts).
  const routeWaypoints = useMemo(
    () =>
      data.origin && data.destination
        ? { origin: data.origin, destination: data.destination, via: [] }
        : undefined,
    [data.origin, data.destination],
  )

  const progress = Math.round(data.progressPct ?? 0)
  const etaLabel = data.etaAt
    ? format(parseISO(data.etaAt), 'dd.MM.yyyy HH:mm')
    : '—'
  const remainingKmLabel =
    data.remainingDistanceM != null
      ? `${Math.max(0, Math.round(data.remainingDistanceM / 1000))} km`
      : '—'

  return (
    <div className="relative h-screen w-screen overflow-hidden">
      <div className="absolute inset-0" data-share-map>
        {truck || routePolylines.length > 0 || routeWaypoints ? (
          <>
            {/* Render the planned trace even before the first live fix; HereMap
                fits to the route bounds when no center/marker is given. */}
            <HereMap
              ref={mapRef}
              vehicles={vehicles}
              routePolylines={routePolylines}
              routeWaypoints={routeWaypoints}
              routeInteractive={false}
              center={truck?.location}
              zoom={truck ? 9 : undefined}
            />
            <ShareMapControls mapRef={mapRef} />
          </>
        ) : (
          <div className="bg-muted flex h-full items-center justify-center">
            <p className="text-muted-foreground text-sm">{t('noRouteData')}</p>
          </div>
        )}
      </div>

      <ShareLanguageSwitcher />

      <div className="bg-background/90 border-border absolute right-3 bottom-3 left-3 z-20 space-y-3 rounded-xl border p-3 shadow-lg backdrop-blur-md md:right-auto md:bottom-4 md:left-4 md:w-[24rem] md:space-y-4 md:p-3.5">
        <div className="flex items-center justify-between gap-2">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <Flag className="text-muted-foreground h-4 w-4 shrink-0" />
            <span className="truncate font-semibold">
              {data.destinationLabel ?? t('destination')}
            </span>
          </div>
          {data.isDelayed ? (
            <Badge
              variant="outline"
              className="text-destructive border-destructive/50 shrink-0"
            >
              {t('delayed')}
            </Badge>
          ) : (
            <Badge
              variant="outline"
              className="shrink-0 border-green-500 text-green-600"
            >
              {t('onTime')}
            </Badge>
          )}
        </div>

        <div className="space-y-1.5">
          <div className="text-muted-foreground flex justify-between text-xs">
            <span>{t('progress')}</span>
            <span className="text-foreground tabular-nums">{progress}%</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
          <div className="flex items-center gap-1.5">
            <Clock className="h-3.5 w-3.5 shrink-0 text-blue-500" />
            <span className="text-muted-foreground">{t('eta')}</span>
            <span className="font-medium whitespace-nowrap tabular-nums">
              {etaLabel}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <Milestone className="h-3.5 w-3.5 shrink-0 text-blue-500" />
            <span className="text-muted-foreground">{t('remaining')}</span>
            <span className="font-medium whitespace-nowrap tabular-nums">
              {remainingKmLabel}
            </span>
          </div>
          {truck?.speed != null && (
            <div className="flex items-center gap-1.5">
              <Gauge className="h-3.5 w-3.5 shrink-0 text-blue-500" />
              <span className="text-muted-foreground">{t('speed')}</span>
              <span className="font-medium whitespace-nowrap">
                {Math.round(truck.speed)} km/h
              </span>
            </div>
          )}
          {data.lastUpdate && (
            <div className="flex items-center gap-1.5">
              <Clock className="text-muted-foreground h-3.5 w-3.5" />
              <span className="text-muted-foreground">{t('updated')}</span>
              <span className="font-medium">
                {format(parseISO(data.lastUpdate), 'HH:mm:ss')}
              </span>
            </div>
          )}
        </div>

        {data.isStale && (
          <p className="text-xs text-amber-600">{t('staleWarning')}</p>
        )}

        <p className="text-muted-foreground border-border/60 border-t pt-2 text-xs">
          {t('linkExpires', {
            date: format(parseISO(data.expiresAt), 'dd.MM.yyyy HH:mm'),
          })}{' '}
          · {t('autoRefresh', { seconds: 3 })}
        </p>
      </div>
    </div>
  )
}
